import { Box } from '@mui/material';
import { useMemo, useState } from 'react';

import { Button } from 'Components/Buttons/Buttons';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_SAVE } from 'Config/Icons';
import { IrqQuestion, IrqQuestionType } from 'Models/InherentRisk';
import { OptionType } from 'Models/Types/GlobalType';

import { Modal } from '../Modal';

export interface AddOrUpdateIrqQuestionModalProps {
    onHideModal: () => void;
    onSaved: (question: IrqQuestion) => void;

    question?: IrqQuestion;
}

const QUESTION_TYPE_OPTIONS: OptionType[] = Object.values(IrqQuestionType)
    .map((value) => ({
        label: value,
        value,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const DEFAULT_QUESTION_TYPE = IrqQuestionType.SINGLE_SELECT;

/**
 * Allows the user to create or update configuration for an IRQ question, defining the question type, text, and possible answers (if a single-select or multiple-select question).
 * Used on the IRQ Configuration page.
 */
export const AddOrUpdateIrqQuestionModal = (props: AddOrUpdateIrqQuestionModalProps) => {
    const [questionText, setQuestionText] = useState(props.question?.text ?? '');
    const [questionType, setQuestionType] = useState<IrqQuestionType>(props.question?._type ?? DEFAULT_QUESTION_TYPE);

    const textIsValid = questionText.trim() !== '';

    const warningText: string | undefined = useMemo(() => {
        if (props.question && (props.question._type === IrqQuestionType.SINGLE_SELECT || props.question._type === IrqQuestionType.MULTIPLE_SELECT) && props.question.options.length > 0 && !(questionType === IrqQuestionType.SINGLE_SELECT || questionType === IrqQuestionType.MULTIPLE_SELECT)) {
            return 'Because the question type is being changed, the existing answer options for this question will be deleted.';
        } else {
            return undefined;
        }
    }, [questionType, props.question]);

    const onSaved = () => {
        props.onSaved(
            (() => {
                switch (questionType) {
                    case IrqQuestionType.DATE:
                        return {
                            _type: IrqQuestionType.DATE,
                            text: questionText,
                            answer: undefined,
                        };
                    case IrqQuestionType.FILE_UPLOAD:
                        return {
                            _type: IrqQuestionType.FILE_UPLOAD,
                            text: questionText,
                            answer: [],
                        };
                    case IrqQuestionType.FREEFORM_TEXT:
                        return {
                            _type: IrqQuestionType.FREEFORM_TEXT,
                            text: questionText,
                            answer: undefined,
                        };
                    case IrqQuestionType.MULTIPLE_SELECT:
                        return {
                            _type: IrqQuestionType.MULTIPLE_SELECT,
                            text: questionText,
                            options: props.question?._type === IrqQuestionType.SINGLE_SELECT || props.question?._type === IrqQuestionType.MULTIPLE_SELECT ? props.question.options : [],
                            answer: [],
                        };
                    case IrqQuestionType.SINGLE_SELECT:
                        return {
                            _type: IrqQuestionType.SINGLE_SELECT,
                            text: questionText,
                            options: props.question?._type === IrqQuestionType.SINGLE_SELECT || props.question?._type === IrqQuestionType.MULTIPLE_SELECT ? props.question.options : [],
                            answer: undefined,
                        };
                }
            })()
        );
        props.onHideModal();
    };

    return (
        <Modal open header={props.question ? 'Update Question' : 'Create Question'}>
            <Box component="form" noValidate>
                <FormFieldSelect required notClearable options={QUESTION_TYPE_OPTIONS} handleChange={(value: ChangeEventType) => setQuestionType(value as IrqQuestionType)} formFieldId="questionType" formFieldLabel="Question Type" selectedOption={questionType} />
                <FormFieldText required value={questionText} formFieldId="questionText" formFieldLabel="Question Text" handleChange={(event: React.FormEvent<HTMLInputElement>) => setQuestionText(event.currentTarget.value)} />
                {warningText && <Text color="red">{warningText}</Text>}
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.onHideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button variant="primary" disabled={!textIsValid} isLoading={false} loadingText="Saving..." fontAwesomeImage={ICON_SAVE} onClick={onSaved}>
                        Save
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
